import type {
  UpdateFileApiResponse,
  UpdateFileBody,
} from "@/common/types/files";
import axiosClient from "@/lib/axios/axiosClient";

export interface FileVersionData {
  id: number;
  result: string;
  fileId: number;
  audioTranscriptionServiceAiId: string;
  serviceAi: string;
  createAt: string;
  transcription: string;
  version: number;
}

export type FileVersionCompact = Pick<
  FileVersionData,
  "id" | "createAt" | "version"
>;

export interface FileData {
  id: number;
  duration: number;
  rowno: number;
  status: string;
  title: string;
  style: string;
  format: string;
  exam: string;
  template: string;
  macros: string;
  createDate: string;
  name: string | null;
  awsBucket?: string | null;
  customTemplate: number | null;
  transcription?: string;
  result?: string;
  original?: string;
  versions: FileVersionCompact[];
}

export interface GetFilesBody {
  userId: number;
  fileId?: number;
  sparse?: number;
}

export const getFiles = async (
  body: GetFilesBody
): Promise<{
  error: boolean;
  files: FileData[];
}> => {
  try {
    const response = await axiosClient.post<FileData[]>("/get-files", body, {
      sendToken: true,
      headers: {
        Accept: "application/json",
        "Content-Type": "text/plain; charset=utf-8",
      },
    });

    const files = response.data;

    if (response.status === 200) {
      if (files) {
        return {
          files,
          error: false,
        };
      } else {
        return {
          files: [],
          error: true,
        };
      }
    } else {
      return {
        files: [],
        error: true,
      };
    }
  } catch {
    return {
      files: [],
      error: true,
    };
  }
};

export interface AddFileBody {
  fileId?: number;
  userId: number;
  name: string;
  awsBucket: string;
  duration: number;
  language?: string;
  style: string;
  format: string;
  exam: string;
  template: string;
  macros?: string;
  customTemplateId: number | null;
  audioTranscriptionServiceAiId?: string;
  diarize?: boolean;
}

export interface AddFileResponseData {
  message: string;
  fileId: number;
}

export const addFile = async (
  body: AddFileBody
): Promise<{
  error: boolean;
  message?: string;
  fileId?: number;
}> => {
  try {
    const response = await axiosClient.post<AddFileResponseData>(
      "/add-file",
      body,
      {
        sendToken: true,
        headers: {
          Accept: "*/*",
          "Content-Type": "application/json",
        },
      }
    );

    const data = response.data;
    if (response.status === 200 && data) {
      return {
        error: false,
        message: data.message,
        fileId: data.fileId,
      };
    } else {
      return {
        error: true,
      };
    }
  } catch (error: unknown) {
    return {
      error: true,
      message:
        (error instanceof Error
          ? error.message
          : "Error desconocido al subir el archivo") || "Error desconocido",
    };
  }
};

export interface AddMultiFileBody {
  userId: number;
  fileId: number;
  name: string;
  duration: number;
}

export const addMultiRecordingFile = async (
  body: AddMultiFileBody
): Promise<{
  error: boolean;
  message?: string;
  fileId?: number;
}> => {
  try {
    const response = await axiosClient.post<AddFileResponseData>(
      "/add-mrfile",
      body,
      {
        sendToken: true,
        headers: {
          Accept: "application/json",
          "Content-Type": "text/plain; charset=utf-8",
        },
      }
    );

    const data = response.data;
    if (response.status === 200 && data) {
      return {
        error: false,
        message: data.message,
        fileId: data.fileId,
      };
    } else {
      return {
        error: true,
      };
    }
  } catch (error: unknown) {
    return {
      error: true,
      message:
        (error instanceof Error
          ? error.message
          : "Error desconocido al subir el archivo") || "Error desconocido",
    };
  }
};

export interface MultiRecordingFileData {
  id: number;
  createDate: string;
  fileId: number;
  name: string;
  transcription: string;
  status: string;
  fileStatus: number;
  duration: number;
}

export const getMultiRecordingFiles = async (body: {
  fileId: number;
}): Promise<{
  error: boolean;
  files: MultiRecordingFileData[];
}> => {
  try {
    const response = await axiosClient.post<MultiRecordingFileData[]>(
      "/get-mrfiles",
      body,
      {
        sendToken: true,
        headers: {
          Accept: "application/json",
          "Content-Type": "text/plain; charset=utf-8",
        },
      }
    );

    const files = response.data;

    if (response.status === 200) {
      return {
        files: files || [],
        error: false,
      };
    } else {
      return {
        files: [],
        error: true,
      };
    }
  } catch {
    return {
      files: [],
      error: true,
    };
  }
};

export const updateFile = async (
  body: UpdateFileBody
): Promise<UpdateFileApiResponse> => {
  const response = await axiosClient.post<UpdateFileApiResponse>(
    "/update-file",
    body,
    {
      sendToken: true,
      headers: {
        Accept: "application/json",
        "Content-Type": "text/plain; charset=utf-8",
      },
    }
  );
  return response.data;
};

export const getFileVersionByVersionId = async (
  fileId: number,
  versionId: number
): Promise<FileVersionData> => {
  const response = await axiosClient.get<FileVersionData>(
    `/visits/${fileId}/versions/${versionId}`,
    {
      sendToken: true,
      headers: {
        Accept: "application/json",
        "Content-Type": "text/plain; charset=utf-8",
      },
    }
  );
  return response.data;
};
